import { motion, AnimatePresence } from 'framer-motion';
import './Modal.css';

export const Modal = ({ isOpen, onClose, content }) => {
  // Convierte un texto con marcadores simples en JSX preservando formato.
  // Soporta líneas que comienzan con '*': título de sección,
  // y líneas que comienzan con '-' para listas.
  const renderFormattedDescription = (text) => {
    if (!text) return null;
    const lines = text.split(/\r?\n/).map(l => l.replace(/\t/g, ' ').trim());
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (!line) { // blank line: skip
        i += 1;
        continue;
      }

      // Heading marker: starts with '*' possibly with no space
      if (line.startsWith('*')) {
        const title = line.replace(/^\*+\s*/, '');
        // gather following non-empty lines as paragraph until next marker
        const paraLines = [];
        let j = i + 1;
        while (j < lines.length && lines[j] && !lines[j].startsWith('*') && !lines[j].startsWith('-')) {
          paraLines.push(lines[j]);
          j += 1;
        }
        blocks.push({ type: 'section', title, text: paraLines.join('\n') });
        i = j;
        continue;
      }

      // List marker: lines starting with '-' create a list block
      if (line.startsWith('-')) {
        const items = [];
        let j = i;
        while (j < lines.length && lines[j] && lines[j].startsWith('-')) {
          items.push(lines[j].replace(/^-+\s*/, ''));
          j += 1;
        }
        blocks.push({ type: 'list', items });
        i = j;
        continue;
      }

      // Fallback paragraph: collect contiguous non-marker lines
      const para = [line];
      let j = i + 1;
      while (j < lines.length && lines[j] && !lines[j].startsWith('*') && !lines[j].startsWith('-')) {
        para.push(lines[j]);
        j += 1;
      }
      blocks.push({ type: 'para', text: para.join('\n') });
      i = j;
    }

    // Render blocks to JSX
    return blocks.map((b, idx) => {
      if (b.type === 'section') {
        return (
          <div key={idx} className="mb-4">
            <h3 className="text-xl md:text-2xl font-semibold mb-1">{b.title}</h3>
            {b.text && <p className="leading-relaxed text-base md:text-lg" style={{ whiteSpace: 'pre-line' }}>{b.text}</p>}
          </div>
        );
      }
      if (b.type === 'list') {
        return (
          <ul key={idx} className="mt-2 ml-4 list-disc space-y-1">
            {b.items.map((it, k) => (
              <li key={k} className="leading-relaxed">{it}</li>
            ))}
          </ul>
        );
      }
      return (
        <p key={idx} className="leading-relaxed text-base md:text-lg" style={{ whiteSpace: 'pre-line' }}>{b.text}</p>
      );
    });
  };
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            // Añadimos 'relative' para posicionar el botón de cerrar
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl relative"
            style={{ color: '#333' }}
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            {/* Botón de cerrar (ícono X) */}
            <button 
              onClick={onClose} 
              className="absolute top-0 right-0 mt-4 mr-4 text-gray-400 hover:text-gray-600 transition"
              aria-label="Cerrar modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>

            {/* Encabezado del Modal */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-blue-900">{content.title}</h2>
            </div>

            {/* Cuerpo del Modal */}
            <div className="p-6 max-h-[70vh] overflow-y-auto modal-privacidad">
              {content.description && (
                <div className="mb-4">
                  {renderFormattedDescription(content.description)}
                </div>
              )}

              {!content.description && content.sections && Array.isArray(content.sections) && (
                <div className="space-y-6">
                  {content.sections.map((sec, sidx) => (
                    <div key={sidx}>
                      <h3 className="text-xl md:text-2xl font-semibold mb-2">{sec.title}</h3>
                      <p className="leading-relaxed text-base md:text-lg" style={{ whiteSpace: 'pre-line' }}>{sec.text}</p>
                    </div>
                  ))}
                </div>
              )}

              {!content.description && content.values && Array.isArray(content.values) && (
                <div className="mt-6 ">
                  <h3 className="text-lg font-semibold">Valores</h3>
                  <ul className="mt-3 space-y-3">
                    {content.values.map((val, idx) => (
                      <li key={idx} className="">
                        <span className="font-semibold">{val.title}:</span>{' '}
                        <span className="leading-relaxed">{val.text}</span>
                      </li>
                    ))}
                  </ul>
                  <hr className="my-4 border-t border-gray-200" />
                </div>
              )}

              {content.services && (
                <div className="mt-4">
                  <ul className="list-disc list-inside">
                    {content.services.map((service, index) => (
                      <li key={index} className="leading-relaxed">{service}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Modal;
